/**
 * The two events around the "this email already has a profile" wall.
 *
 * Measured on production over 18-22/09/2026: about 133 people a day reach `/login` carrying
 * the apply intent, 27 finish signing in and 17 come back to the offer. Everything before
 * `/login` was invisible, so the denominator had to be reconstructed from the
 * `cv_upload_intents` table by hand. These events make it readable in PostHog.
 *
 * Shapes only here, so the three call sites cannot drift apart: an event whose properties
 * differ by call site is worse than no event, because the difference does not show up as an
 * error, only as a segment that quietly disappears from a breakdown.
 */

/** Which way out of the wall was taken, and whether it was offered before the CV or after it. */
export type ExistingAccountPlacement = 'pre_cv' | 'post_409';

/** Which credential already belongs to an account. */
export type ExistingAccountConflict = 'email' | 'phone';

/** Which of the two entry points the visitor picked on the first screen. */
export type ExistingAccountEntryType = 'cv' | 'questionario' | 'unknown';

/**
 * `entryType` is the modal's own tab name; it is left unset when the wall is reached by a
 * path that never went through the first screen, and `unknown` keeps that case countable
 * instead of silently folding it into `cv`.
 */
export const entryTypeOf = (entryType?: string): ExistingAccountEntryType => {
  if (entryType === 'upload-cv') return 'cv';
  if (entryType === 'application-wizard') return 'questionario';
  return 'unknown';
};

/**
 * Which wall, if any, the error payload is currently showing.
 *
 * The order matters and is the component's: a payload can carry both a 409 and a phone
 * conflict, and the phone screen is the one rendered, so reporting `email` there would count
 * a screen nobody saw. `null` is every other error, above all the unreadable-CV one, which is
 * a different problem with a different fix and must not inflate this denominator.
 */
export const conflictFromErrors = (errors?: {
  phone_number?: string;
  statusCode?: number;
}): ExistingAccountConflict | null => {
  if (errors?.phone_number?.includes('already exists')) return 'phone';
  if (errors?.statusCode === 409) return 'email';
  return null;
};

export const wallShownProperties = ({
  jobPositionId,
  entryType,
  conflict,
}: {
  jobPositionId?: string;
  entryType?: string;
  conflict: ExistingAccountConflict;
}) => ({
  job_position_id: jobPositionId ?? null,
  entry_type: entryTypeOf(entryType),
  conflict,
});

export const loginClickedProperties = ({
  jobPositionId,
  placement,
  methodHint,
}: {
  jobPositionId?: string;
  placement: ExistingAccountPlacement;
  /** Which credential the login screen will be prefilled with, when the wall knows one. */
  methodHint?: ExistingAccountConflict;
}) => ({
  job_position_id: jobPositionId ?? null,
  placement,
  method_hint: methodHint ?? null,
});
